import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getDetectedLanguage } from "../../hooks/useLanguageSync";

const SUPPORTED_LNGS = ["en", "es", "fr"];

// Private/protected route prefixes that are mounted *without* a language segment
// in App.tsx. If a stale link or legacy navigate() points at /<lang>/dashboard,
// we strip the lang prefix instead of bouncing the user back to the homepage —
// which would silently skip PostRegistrationContainer and the membership selector.
const PROTECTED_PREFIXES = [
  "/dashboard",
  "/specialist",
  "/admin",
  "/group-admin",
  "/teleconsult",
  "/membership-selection",
];

/**
 * Componente que redirige automáticamente a la ruta con el idioma detectado
 * - Detecta el idioma del navegador
 * - Si es soportado (en, es, fr), redirige a esa versión
 * - Si no es soportado, redirige a inglés por defecto
 */
export const LanguageRedirect: React.FC = () => {
  const location = useLocation();
  const [detectedLang, setDetectedLang] = useState<string | null>(null);

  useEffect(() => {
    const lang = getDetectedLanguage();
    setDetectedLang(lang);
  }, []);

  // Preservar la query string y hash si existen
  const searchParams = location.search || "";
  const hash = location.hash || "";

  // If the URL is /<lang>/<protected> (e.g. /en/dashboard), strip the lang
  // segment so the request matches the flat private route in App.tsx. Without
  // this, /en/dashboard falls through to the catch-all and would redirect to
  // /<lang> (homepage), bypassing PostRegistrationContainer entirely.
  const segments = location.pathname.split("/").filter(Boolean);
  if (segments.length >= 2 && SUPPORTED_LNGS.includes(segments[0])) {
    const stripped = "/" + segments.slice(1).join("/");
    if (PROTECTED_PREFIXES.some((p) => stripped === p || stripped.startsWith(p + "/"))) {
      return <Navigate to={`${stripped}${searchParams}${hash}`} replace />;
    }
  }

  if (!detectedLang) {
    // Mostrar nada mientras detecta el idioma
    return null;
  }

  return <Navigate to={`/${detectedLang}${searchParams}${hash}`} replace />;
};

export default LanguageRedirect;
